"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getNextActionIdsFromActionWithBranches = getNextActionIdsFromActionWithBranches;
exports.mergeSpecialEdgeRules = mergeSpecialEdgeRules;
var _functions = require("../utility/functions");
var _ = require(".");
const EVENT_RESULT_TYPES = {
  PREFIX: 'PREFIX',
  OPTIONAL: 'OPTIONAL',
  OUTCOME: 'OUTCOME'
};
/**
 * addStartTimeAndBranchNameToEventResults - add the action start times (the time between when the parent action starts and when the eventResults occur) and branchName to the eventResult list
 *
 * @param {EventResult<IdType>} eventResult - the event result to add start times to
 * @param {number} startTime - the time (from the parent action) the eventResult is expected to run
 * @param {string} branchName - the name of the branch the action is a part of
 *
 * @return {EventResultWithStartTimeData<IdType>} - the eventResults also containing the expected start time of the actions
 */
function addStartTimeAndBranchNameToEventResults(eventResult, startTime, groupId, groupType, specialEdgeRules = null) {
  return {
    type: groupType,
    id: groupId,
    actionList: eventResult.map(actionId => ({
      actionId,
      startTime,
      groupId,
      specialEdgeRules
    }))
  };
}

/**
 * getNextActionIdsFromActionWithBranches - Returns the outcome (resulting action ids) of the given action grouped by how the actions are handled.
 * Does NOT use recursion.
 *
 * @param {Action<IdType>} action - the action to get the outcomes for
 *
 * @returns <ActionOutcomes<IdType>> - check type definition for description of what each field means
 */
function getNextActionIdsFromActionWithBranches(action) {
  const actionDuration = (0, _.getActionDuration)(action).actionDuration ?? 0;
  const prefixes = {};
  const outcomes = {};
  const optionals = {};
  Object.keys(EVENT_RESULT_OUTCOMES_DETERMINERS).forEach(key => {
    if (!action.hasOwnProperty(key)) {
      return;
    }
    // $FlowFixMe[prop-missing]: Flow doesn't like how we intersect the keys to find the correct values of the actions
    const results = EVENT_RESULT_OUTCOMES_DETERMINERS[key](action, actionDuration);
    for (let result of results) {
      let actionCollection;
      if (result.type === EVENT_RESULT_TYPES.OUTCOME) {
        outcomes[result.id] ??= {};
        actionCollection = outcomes[result.id];
      } else if (result.type === EVENT_RESULT_TYPES.OPTIONAL) {
        optionals[result.id] ??= {};
        actionCollection = optionals[result.id];
      } else {
        actionCollection = prefixes;
      }
      for (let actionData of result.actionList) {
        actionCollection[String(actionData.actionId)] ??= actionData;
      }
    }
  });
  return {
    prefix: (0, _functions.values)(prefixes),
    optionals: Object.keys(optionals).map(optionId => ({
      id: optionId,
      actionIds: (0, _functions.values)(optionals[optionId])
    })),
    outcomes: Object.keys(outcomes).map(outcomeId => ({
      id: outcomeId,
      actionIds: (0, _functions.values)(outcomes[outcomeId])
    }))
  };
}

/**
 * mergeSpecialEdgeRules - merge a list of specialRules into a single specialEdgeRule. The result is a SpecialEdgeRule of
 * where a rule will be true if any of the passed edge rules had the rule as true.
 *
 * @param {$ReadOnlyArray<?SpecialRules>} specialEdgeRules - a list of the special edge rules to merge
 *
 * @return {?SpecialRules}
 */
function mergeSpecialEdgeRules(specialEdgeRules) {
  let mergedSpecialRules = null;
  for (let specialRule of specialEdgeRules) {
    if (!specialRule) {
      continue;
    }
    const definedSpecialRules = Object.keys(specialRule);
    if (!definedSpecialRules.length) {
      continue;
    }
    if (!mergedSpecialRules) {
      mergedSpecialRules = {
        ...specialRule
      };
      continue;
    }
    for (let rule of definedSpecialRules) {
      mergedSpecialRules[rule] ||= specialRule[rule];
    }
  }
  return mergedSpecialRules;
}

/**
 * A mapping of keys in actions to functions which can group the eventResults in those fields.
 */
const EVENT_RESULT_OUTCOMES_DETERMINERS = {
  onStart: a => [addStartTimeAndBranchNameToEventResults(a.onStart, 0, 'onStart', EVENT_RESULT_TYPES.PREFIX)],
  onEnd: (a, actionDuration) => [addStartTimeAndBranchNameToEventResults(a.onEnd, actionDuration, 'onEnd', a.type === 'CULMINATING_MOMENT_SCREEN' || a.type === 'COLLABORATIVE_CULMINATING_MOMENT_SCREEN' ?
  // onEnd actions are ran regardless of the outcome of the action in these Actions
  EVENT_RESULT_TYPES.PREFIX : EVENT_RESULT_TYPES.OUTCOME)],
  onComplete: (a, actionDuration) => [addStartTimeAndBranchNameToEventResults(a.onComplete, actionDuration, 'onComplete', EVENT_RESULT_TYPES.OUTCOME)],
  onFailure: (a, actionDuration) => [addStartTimeAndBranchNameToEventResults(a.onFailure, actionDuration, 'onFailure', EVENT_RESULT_TYPES.OUTCOME)],
  onSuccess: (a, actionDuration) => [addStartTimeAndBranchNameToEventResults(a.onSuccess, actionDuration, 'onSuccess', EVENT_RESULT_TYPES.OUTCOME)],
  atTime: a => {
    return [...a.atTime].sort((a, b) => a.timestamp - b.timestamp).map((allTimeActions, time) => {
      return addStartTimeAndBranchNameToEventResults(allTimeActions.actions, allTimeActions.timestamp, 'atTime', EVENT_RESULT_TYPES.PREFIX);
    });
  },
  onVoteStart: (a, actionDuration) => [addStartTimeAndBranchNameToEventResults(a.onVoteStart, actionDuration, 'onVoteStart', EVENT_RESULT_TYPES.PREFIX)],
  questionInfo: (a, actionDuration) => {
    return a.questionInfo.options.map((option, i) => addStartTimeAndBranchNameToEventResults(
    // $FlowExpectedError[incompatible-cast] QuestionInfo is typed incorrectly
    option.result, actionDuration, `Option ${i + 1}`, EVENT_RESULT_TYPES.OUTCOME));
  },
  onContinue: (a, actionDuration) => [addStartTimeAndBranchNameToEventResults(a.onContinue || [], actionDuration, 'onContinue', EVENT_RESULT_TYPES.PREFIX)],
  stationData: (a, actionDuration) => {
    const onCompleteResult = a.stationData.onComplete;
    if (onCompleteResult) {
      if (onCompleteResult.length === 0) {
        return [addStartTimeAndBranchNameToEventResults(onCompleteResult, actionDuration, 'onStationComplete/NotComplete (no actions)', EVENT_RESULT_TYPES.OUTCOME)];
      }
      return [addStartTimeAndBranchNameToEventResults(onCompleteResult, actionDuration, 'onStationComplete', EVENT_RESULT_TYPES.OUTCOME), addStartTimeAndBranchNameToEventResults([], actionDuration, 'stationDidNotComplete',
      // handle when the station does not complete
      EVENT_RESULT_TYPES.OUTCOME)];
    }

    // Don't get EventResults from the Creative Canvas station because those will be resolved during the collaborative culminating moment
    return [addStartTimeAndBranchNameToEventResults([], actionDuration, 'onStationComplete', EVENT_RESULT_TYPES.PREFIX)];
  },
  onCheatDeath: (a, actionDuration) => [addStartTimeAndBranchNameToEventResults(a.onCheatDeath || [], actionDuration, 'onCheatDeath', EVENT_RESULT_TYPES.OUTCOME, {
    breakOnEndPath: true // do not allow following this edge when calculating paths to end in the condensedActionGraph. See `SpecialEdgeRules` type definition for more info.
  })],
  onDeath: (a, actionDuration) => [addStartTimeAndBranchNameToEventResults(a.onDeath || [], actionDuration, 'onDeath', EVENT_RESULT_TYPES.OUTCOME)],
  onPlayerCollisionWithSolid: (a, actionDuration) => [addStartTimeAndBranchNameToEventResults(a.onPlayerCollisionWithSolid || [], actionDuration, 'onPlayerCollisionWithSolid', EVENT_RESULT_TYPES.OPTIONAL)]
};